import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

export type UnsubscribeContactsPayload = {
  includedContactIds?: number[]
  includedGroupIds?: number[]
}

/**
 * Unsubscribe multiple contacts from a campaign
 */
export const useUnsubscribeContacts = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [unsubscribeContacts, res] = esApi.endpoints.unsubscribeContacts.useMutation()

  const unsubscribeContactsFromCampaign = useCallback(
    async (campaignId: number, payload: UnsubscribeContactsPayload) => {
      try {
        const result = await unsubscribeContacts({
          orgId: orgId.toString(),
          campaignId: campaignId.toString(),
          body: {
            contacts: {
              includedContactIds: payload.includedContactIds,
              includedGroupIds: payload.includedGroupIds,
            },
          },
        }).unwrap()

        dispatch(
          createNotification({
            'unsubscribe-campaign-contacts': {
              type: 'success',
              message: `${result.total || 0} contacts successfully unsubscribed from campaign.`,
            },
          }),
        )
        return result
      } catch (error) {
        const { includedGroupIds } = payload
        const fallbackMessage = `A problem occurred with trying to unsubscribe the ${
          includedGroupIds?.length ? 'Group' : 'Contact'
        } from the campaign. Please try again later.`
        dispatch(
          createNotification({
            'unsubscribe-campaign': {
              type: 'error',
              message: getErrorMessage(error, fallbackMessage),
            },
          }),
        )
        return undefined
      }
    },
    [orgId, unsubscribeContacts, dispatch],
  )

  return [unsubscribeContactsFromCampaign, res] as const
}
